import type { WidgetChoice, WidgetSize } from '../../lib/preferences'

/**
 * Where a dragged widget should land, decided from measured rectangles rather than from the DOM.
 *
 * Pure on purpose. The pointer handling in `DashboardPage` is a few lines of listener plumbing that
 * cannot be tested without a browser, and the rules for what a drag actually means - which card is
 * under the pointer, whereabouts on it, and what that does to the widths of everything sharing that
 * row - are the part worth being sure about. Splitting them is how everything else here is arranged
 * (D-186).
 *
 * Dragging is offered *beside* the move buttons and the width control, never instead of them. It is
 * unreachable from a keyboard and awkward on a touch screen, so it is the faster way to do what those
 * already do rather than the only way.
 */
export interface WidgetBox {
  widget: string
  left: number
  top: number
  width: number
  height: number
}

/** How many of the grid's six columns each width takes. */
export const SPAN: Record<WidgetSize, number> = { full: 6, half: 3, third: 2 }

export const COLUMNS = 6

/** A missing width means the catalog's, and every catalog default this board has is a width. */
const spanOf = (choice: WidgetChoice): number => SPAN[(choice.size ?? 'full') as WidgetSize] ?? SPAN.full

/**
 * What dropping on a particular part of a card means.
 *
 * The two edges put the dragged widget *beside* the one under the pointer; the middle puts it in that
 * position in the order and leaves every width alone. A quarter of the width each side is enough to
 * aim at without making the ordinary reorder - by far the commoner gesture - hard to hit.
 */
export type DropZone = 'before' | 'after' | 'beside-before' | 'beside-after'

export function zoneFor(box: WidgetBox, x: number): DropZone {
  const offset = (x - box.left) / box.width
  if (offset < 0.25) return 'beside-before'
  if (offset > 0.75) return 'beside-after'
  return offset < 0.5 ? 'before' : 'after'
}

/**
 * The widget whose box contains the point, or null.
 *
 * The board is a grid, so the gaps between cards are real and a pointer in one is over nothing. That
 * is reported as nothing rather than resolved to the nearest card: a drag that keeps reordering while
 * the pointer sits in a gutter is one that reorders when somebody is holding still.
 */
export function widgetAt(boxes: readonly WidgetBox[], x: number, y: number): WidgetBox | null {
  for (const box of boxes) {
    if (x >= box.left && x < box.left + box.width && y >= box.top && y < box.top + box.height) return box
  }

  return null
}

/**
 * Packs the visible widgets into rows exactly as the CSS grid does.
 *
 * The grid is the authority on what "beside" means, and it decides by filling six columns and
 * wrapping. Reimplementing that here rather than measuring the rendered rows is deliberate: a drop
 * has to know which widgets will share a row *after* the move, and the only way to measure that would
 * be to perform the move and look.
 */
export function rows(layout: readonly WidgetChoice[]): WidgetChoice[][] {
  const packed: WidgetChoice[][] = []
  let row: WidgetChoice[] = []
  let used = 0

  for (const choice of layout) {
    const span = spanOf(choice)
    if (used + span > COLUMNS && row.length > 0) {
      packed.push(row)
      row = []
      used = 0
    }

    row.push(choice)
    used += span
  }

  if (row.length > 0) packed.push(row)
  return packed
}

/** The width that lets `count` widgets share one row, or null when that many cannot. */
export function widthForRow(count: number): WidgetSize | null {
  if (count <= 1) return 'full'
  if (count === 2) return 'half'
  if (count === 3) return 'third'
  return null
}

/** Moves `dragged` to sit immediately before or after `target`, leaving every width alone. */
function reorder(layout: readonly WidgetChoice[], dragged: string, target: string, after: boolean): WidgetChoice[] {
  const without = layout.filter((choice) => choice.widget !== dragged)
  const moving = layout.find((choice) => choice.widget === dragged)
  const at = without.findIndex((choice) => choice.widget === target)
  if (!moving || at < 0) return [...layout]

  const next = [...without]
  next.splice(after ? at + 1 : at, 0, moving)
  return next
}

/**
 * Applies a drop, which is either a reorder or a request to share a row.
 *
 * Sharing a row re-widths every widget already in the target's row as well as the one being dropped,
 * because that is the only way three panels can end up beside each other: the grid wraps whatever
 * does not fit, so leaving the neighbours at half and adding a third would push it onto a line of its
 * own and look exactly like the drop having been ignored.
 *
 * A row that cannot take another - four would need a column and a half each - falls back to a plain
 * reorder rather than refusing the drop. So does a widget the catalog will not draw at the width the
 * row needs, which is how the ribbon stays full width without being a special case here.
 */
export function applyDrop(
  layout: readonly WidgetChoice[],
  dragged: string,
  target: string,
  zone: DropZone,
  sizes: (widget: string) => readonly WidgetSize[]
): WidgetChoice[] {
  if (dragged === target) return [...layout]
  if (!layout.some((choice) => choice.widget === dragged)) return [...layout]
  if (!layout.some((choice) => choice.widget === target)) return [...layout]

  const after = zone === 'after' || zone === 'beside-after'
  if (zone === 'before' || zone === 'after') return reorder(layout, dragged, target, after)

  // The row as it will be once the dragged widget has left it, which is not the same row it is in now.
  const without = layout.filter((choice) => choice.widget !== dragged)
  const row = rows(without).find((candidate) => candidate.some((choice) => choice.widget === target))
  if (!row) return reorder(layout, dragged, target, after)

  const sharing = [...row.map((choice) => choice.widget), dragged]
  const width = widthForRow(sharing.length)
  if (!width || sharing.some((widget) => !sizes(widget).includes(width))) {
    return reorder(layout, dragged, target, after)
  }

  const widened = reorder(layout, dragged, target, after)
  return widened.map((choice) => (sharing.includes(choice.widget) ? { ...choice, size: width } : choice))
}

/** Whether two layouts describe the same widgets in the same order at the same widths. */
export function sameArrangement(left: readonly WidgetChoice[], right: readonly WidgetChoice[]): boolean {
  return (
    left.length === right.length &&
    left.every((choice, index) => choice.widget === right[index]?.widget && choice.size === right[index]?.size)
  )
}
